<template>
  <div>
    <TitleWrapper
      :filter-option="false"
      title="SUBJECT_RESULT_DETAILS"
      tooltip-title="SUBJECT_RESULT_DETAILS"
      :display-breadcrumb="true"
    />
    <div v-if="isLoading">
      <Loader :content="true" />
    </div>
    <div v-else class="w-full mt-5">
      <DetailCard :details="resultDetails" custom-classes="flex-col" />

      <div>
        <Heading heading="Exams" heading-size="pb-0 text-xl" />
        <div v-if="isEmpty(examList)">
          <NoRecordFound class="mt-28" />
        </div>
        <TableWrapper v-else>
          <THead>
            <TRHead>
              <TH v-for="(head, index) in filteredTableHeaders" :key="index">
                {{ head }}
              </TH>
            </TRHead>
          </THead>
          <TBody>
            <TRBody v-for="(result, index) in examList" :key="index">
              <TD>
                {{
                  result?.subject_exam_schedule?.exam_schedule?.title ||
                  GENERAL_CONSTANTS.NOT_APPLICABLE
                }}
              </TD>
              <TD>{{ result.weightage }}</TD>
              <TD v-if="!activeStudent" v-permission="tableActionsPermissions">
                <TableAction
                  :idx="index"
                  :current-user="result"
                  :action-list="actionList"
                  @action="typeAction"
                />
              </TD>
            </TRBody>
          </TBody>
        </TableWrapper>
        <Pagination
          v-if="resultCounts > GENERAL_CONSTANTS.RECORD_LIMIT"
          v-show="!isLoading"
          :record-limit="GENERAL_CONSTANTS.RECORD_LIMIT"
          :max-range="paginationCounts(resultCounts, GENERAL_CONSTANTS.RECORD_LIMIT)"
          @filterRecord="filteredRecords"
        />
      </div>
      <div>
        <Heading heading="Assignments" heading-size="pb-0 text-xl" />
        <div v-if="isEmpty(assignmentsList)">
          <NoRecordFound class="mt-28" />
        </div>
        <TableWrapper v-else>
          <THead>
            <TRHead>
              <TH v-for="(head, index) in filteredAssignmentTableHeaders" :key="index">
                {{ head }}
              </TH>
            </TRHead>
          </THead>
          <TBody>
            <TRBody v-for="(result, index) in assignmentsList" :key="index">
              <TD>
                {{ result?.assignment?.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
              </TD>
              <TD>{{ result.weightage }}</TD>
              <TD v-if="!activeStudent" v-permission="tableActionsPermissions">
                <TableAction
                  :idx="index"
                  :current-user="result"
                  :action-list="actionList"
                  @action="typeActionAssignment"
                />
              </TD>
            </TRBody>
          </TBody>
        </TableWrapper>
        <Pagination
          v-if="resultCounts > GENERAL_CONSTANTS.RECORD_LIMIT"
          v-show="!isLoading"
          :record-limit="GENERAL_CONSTANTS.RECORD_LIMIT"
          :max-range="paginationCounts(resultCounts, GENERAL_CONSTANTS.RECORD_LIMIT)"
          @filterRecord="filteredRecords"
        />
      </div>
    </div>
    <UpdateResultModal
      v-if="isModalView"
      :modal="isModalView"
      :result="currentResult"
      @toggle="toggleUpdateExamWeightageModal"
    />
    <UpdateResultModal
      v-if="isAssignmentEditModal"
      :modal="isAssignmentEditModal"
      :assignment="true"
      :result="currentResult"
      @toggle="toggleUpdateAssignmentWeightageModal"
    />
    <UIConfirmationModal
      v-if="generteResultModal"
      :modal="generteResultModal"
      heading="GENERATE_RESULT"
      message="Are you sure you want to generate result for "
      button-text="Confirm"
      :name="resultDetails?.title"
      @cancel="toggleGenerateResultModal"
      @confirm="generateResultForSubject"
    />
    <UIConfirmationModal
      v-if="reGenerteResultModal"
      :modal="reGenerteResultModal"
      heading="REGENERATE_RESULT"
      message="Are you sure you want to re generate result for "
      button-text="Confirm"
      :name="resultDetails?.title"
      @cancel="toggleReGenerateResultModal"
      @confirm="reGenerateResultForSubject"
    />
  </div>
</template>

<script>
import TitleWrapper from '@components/TitleWrapper.vue'
import { mapActions, mapState } from 'vuex'
import GENERAL_CONSTANTS from '@src/constants/general-constants'
import Loader from '@components/BaseComponent/Loader.vue'
import DATE_TIME_CONSTANT from '@src/constants/date-time-constants.js'
import scrollMixin from '@src/mixins/scroll-mixin'
import Heading from '@components/Heading.vue'
import UpdateResultModal from '@src/router/views/exam-planner/generate-subject-exam-result/UpdateWeightageModal.vue'
import { toPascalCase } from '@utils/generalUtil'
import TableAction from '@components/TableAction.vue'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import { paginationCounts } from '@src/components/BaseComponent/pagination.util.js'
import isEmpty from 'lodash/isEmpty'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'
import UIConfirmationModal from '@src/components/UiElements/UIConfirmationModal.vue'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import DetailCard from '@/src/components/UiElements/DetailCard.vue'

export default {
  components: {
    TitleWrapper,
    Loader,
    Heading,
    UpdateResultModal,
    TableAction,
    NoRecordFound,
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
    DetailCard,
    UIConfirmationModal,
  },

  mixins: [scrollMixin],
  data() {
    return {
      tableActionsPermissions: {
        roles: [TENANT_ROLES.SUPER_ADMIN, TENANT_ROLES.CAMPUS_ADMIN, TENANT_ROLES.SECTION_TEACHER],
      },
      GENERAL_CONSTANTS,
      DATE_TIME_CONSTANT,
      tableHeaders: ['Exam', 'Weightage', 'Actions'],
      assignmentsTableHeaders: ['Assignment', 'Weightage', 'Actions'],
      isLoading: false,
      isModalView: false,
      isAssignmentEditModal: false,
      actionList: [{ name: 'Edit' }],
      resultDetails: {},
      currentSelectedSubject: null,
      resultCounts: 0,
      examList: [],
      assignmentsList: [],
      isResultGenerated: null,
      generteResultModal: false,
      currentSelectedResult: null,
      examId: null,
      reGenerteResultModal: false,
    }
  },
  page: {
    title: 'Exam Subject Result',
  },
  computed: {
    ...mapState({
      activeStudent: (state) => state.student.activeStudent,
    }),
    ...mapState('layout', ['currentClassScope', 'currentSectionScope', 'activeRole']),
    currentResultId() {
      return this.$route.params.id
    },
    filteredTableHeaders() {
      const hasActionsPermission = this.tableActionsPermissions.roles.includes(this.activeRole)
      const showActions = hasActionsPermission && !this.activeStudent
      return showActions
        ? [...this.tableHeaders]
        : this.tableHeaders.filter((header) => header !== 'Actions')
    },
    filteredAssignmentTableHeaders() {
      const hasActionsPermission = this.tableActionsPermissions.roles.includes(this.activeRole)
      const showActions = hasActionsPermission && !this.activeStudent
      return showActions
        ? [...this.assignmentsTableHeaders]
        : this.assignmentsTableHeaders.filter((header) => header !== 'Actions')
    },
  },
  watch: {
    currentClassScope: {
      handler() {
        this.$router.push({ name: 'section-result' })
      },
      deep: true,
    },
    currentSectionScope: {
      handler() {
        this.$router.push({ name: 'section-result' })
      },
      deep: true,
    },
    isResultGenerated: {
      handler() {
        this.setRightbarData()
      },
    },
  },
  mounted() {
    this.filteredRecords()
    this.setRightbarData()
  },
  methods: {
    isEmpty,
    paginationCounts,
    toPascalCase,
    toggleUpdateExamWeightageModal(payload) {
      this.isModalView = !this.isModalView
      if (!this.isModalView) this.currentResult = null
      if (payload) this.filteredRecords()
    },
    toggleUpdateAssignmentWeightageModal(payload) {
      this.isAssignmentEditModal = !this.isAssignmentEditModal
      if (!this.isAssignmentEditModal) this.currentResult = null
      if (payload) this.filteredRecords()
    },

    toggleGenerateResultModal() {
      this.generteResultModal = !this.generteResultModal
      if (!this.generteResultModal) this.currentSelectedResult = null
    },
    toggleReGenerateResultModal() {
      this.reGenerteResultModal = !this.reGenerteResultModal
      if (!this.reGenerteResultModal) this.currentSelectedResult = null
    },
    async reGenerateResultForSubject() {
      let [response, error] = await this.reGenerateSubjectResult(this.examId)
      if (response) {
        this.toggleReGenerateResultModal()
        this.filteredRecords()
        this.$store.commit(
          'toast/NEW',
          { message: response.data.message, type: 'success' },
          { root: true },
        )
      }
    },

    async generateResultForSubject() {
      let [response, error, errorList] = await this.generateSubjectResult(this.examId)
      if (response) {
        this.toggleGenerateResultModal()
        this.filteredRecords()
        this.$store.commit(
          'toast/NEW',
          { message: response.data.message, type: 'success' },
          { root: true },
        )
      }
    },

    async filteredRecords(range) {
      this.isLoading = true
      let [response, error] = await this.getSubjectExamDetails(this.currentResultId)
      this.isResultGenerated = response.data.generated_at
      this.examId = response.data.id
      this.resultDetails = this.getDetails(response.data)
      this.examList = response.data.subject_exam_schedule_weightages
      this.assignmentsList = response.data.subject_exam_assignment_weightages
      this.isLoading = false
    },
    getDetails(details) {
      return {
        title: details?.title,
        gradingStructure: details.non_gpa_grading?.title,
        subject: details.subject?.title,
        passingPercentage: details.passing_percentage,
        totalMarks: details.total_marks,
      }
    },
    typeAction(action, currentType, idx) {
      this.currentResult = currentType
      switch (action) {
        case 'Edit':
          this.toggleUpdateExamWeightageModal()
          break
      }
    },
    typeActionAssignment(action, currentType, idx) {
      this.currentResult = currentType
      switch (action) {
        case 'Edit':
          this.toggleUpdateAssignmentWeightageModal()
          break
      }
    },

    viewResult() {
      this.$router?.push({
        name: 'subject-resul-detail-view',
        params: { id: this.examId },
      })
    },
    setRightbarData() {
      const sharedRoles = [
        TENANT_ROLES.SUPER_ADMIN,
        TENANT_ROLES.CAMPUS_ADMIN,
        TENANT_ROLES.SECTION_TEACHER,
      ]

      const buttons = [
        {
          title: 'VIEW_RESULT',
          classes: ['primary-button-right'],
          action: {
            func: () => this.viewResult(),
          },
          permissions: {
            roles: sharedRoles,
          },
        },
        this.isResultGenerated
          ? {
              title: 'REGENERATE_RESULT',
              classes: ['primary-button-right'],
              action: {
                func: () => this.toggleReGenerateResultModal(),
              },
              permissions: {
                roles: sharedRoles,
              },
            }
          : {
              title: 'GENERATE_RESULT',
              classes: ['primary-button-right'],
              action: {
                func: () => this.toggleGenerateResultModal(),
              },
              permissions: {
                roles: sharedRoles,
              },
            },
      ]

      const rightBarContent = {
        header: {
          title: 'SUBJECT_RESULT_DETAILS',
          buttons,
        },
        stats: [
          {
            title: GENERAL_CONSTANTS.STATS_AND_SUMMARY,
            categories: [
              {
                text: 'Total_RESULT',
                value: this.resultCounts,
              },
            ],
          },
        ],
      }

      this.setRightbarContent(rightBarContent)
    },
    ...mapActions('layout', ['setRightbarContent']),
    ...mapActions('exams', [
      'getSubjectExamDetails',
      'generateSubjectResult',
      'reGenerateSubjectResult',
    ]),
  },
}
</script>
